// SPDX-License-Identifier: Elastic-2.0
/**
 * useLocale — Zustand store wrapping i18next's active language.
 *
 * Responsibilities (functional spec § 9):
 *  - Resolve the initial locale once at module load by inspecting
 *    `localStorage.locale` first, then `navigator.language`, then
 *    falling back to EN. Only values in SUPPORTED_LOCALES are honoured.
 *  - Expose `setLocale()` which:
 *      1. lazy-loads the locale JSON (no-op for EN),
 *      2. calls `i18next.changeLanguage()`,
 *      3. persists to `localStorage.locale`,
 *      4. mirrors the value onto `<html lang="…">`.
 *  - Subscribe to i18next's `languageChanged` event so external callers
 *    (e.g. a future debug panel that runs `i18next.changeLanguage()`
 *    directly) keep the store in sync.
 *
 * The inline `<script>` in index.html sets `<html lang>` BEFORE React
 * mounts to avoid a flash of incorrect lang attribute — this store
 * re-applies it on every change to cover the runtime-switch case.
 */
import { create } from "zustand";

import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  i18next,
  isSupportedLocale,
  loadLocale,
  type SupportedLocale,
} from "./index";

/** Public store shape. */
export interface LocaleState {
  /** Currently active locale (mirrors i18next.language, normalized). */
  locale: SupportedLocale;
  /** All locales the app supports — handy for the language-picker UI. */
  supported: readonly SupportedLocale[];
  /**
   * Switch the active locale.
   * - Lazy-loads the locale JSON if not already loaded.
   * - Persists to localStorage and updates `<html lang>`.
   * - No-op (resolves immediately) if the value is already active.
   */
  setLocale: (next: SupportedLocale) => Promise<void>;
}

/** Read the locale from localStorage if present + valid. */
function readStoredLocale(): SupportedLocale | null {
  try {
    const raw = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(raw) ? raw : null;
  } catch {
    // localStorage may throw in private-mode Safari, etc. Treat as absent.
    return null;
  }
}

/** Read the navigator's preferred language, mapped to a supported locale. */
function readNavigatorLocale(): SupportedLocale | null {
  const langs: readonly string[] =
    typeof navigator !== "undefined" && Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : typeof navigator !== "undefined" && typeof navigator.language === "string"
        ? [navigator.language]
        : [];
  for (const full of langs) {
    const base = full.split("-")[0]?.toLowerCase();
    if (isSupportedLocale(base)) {
      return base;
    }
  }
  return null;
}

/** Resolve the initial locale: stored → navigator → default. */
export function resolveInitialLocale(): SupportedLocale {
  return readStoredLocale() ?? readNavigatorLocale() ?? DEFAULT_LOCALE;
}

/** Apply `<html lang>` if the document is available (SSR-safe). */
function applyHtmlLang(locale: SupportedLocale): void {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("lang", locale);
  }
}

/** Persist the locale choice (silent on storage failures). */
function persistLocale(locale: SupportedLocale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore — quota-exceeded or disabled storage is non-fatal.
  }
}

const initialLocale = resolveInitialLocale();

/**
 * The store. Created once at module load.
 *
 * We kick off the lazy-load + language switch for the initial locale
 * (if non-EN) outside the store body so the export is synchronous.
 */
export const useLocale = create<LocaleState>((set, get) => ({
  locale: initialLocale,
  supported: SUPPORTED_LOCALES,
  setLocale: async (next: SupportedLocale) => {
    if (!isSupportedLocale(next)) {
      // Defensive: ignore unknown locales instead of throwing.
      return;
    }
    if (
      get().locale === next &&
      i18next.language === next &&
      i18next.hasResourceBundle(next, "translation")
    ) {
      // Already active — still ensure persistence + lang attr in case
      // either drifted (e.g. test setup cleared localStorage).
      persistLocale(next);
      applyHtmlLang(next);
      return;
    }
    await loadLocale(next);
    await i18next.changeLanguage(next);
    persistLocale(next);
    applyHtmlLang(next);
    set({ locale: next });
  },
}));

// Apply the initial locale to <html lang>; the inline script in index.html
// already did this on first paint, but we re-run here so unit tests (which
// boot without the inline script) still see a correct value.
applyHtmlLang(initialLocale);

// If the initial locale is non-EN, lazy-load + switch. Main boot awaits this
// before the first React render so persisted locale choices apply immediately.
export const initialLocaleReady: Promise<void> =
  initialLocale !== DEFAULT_LOCALE
    ? useLocale.getState().setLocale(initialLocale)
    : Promise.resolve();

/**
 * Keep the store in sync if anyone calls `i18next.changeLanguage()`
 * directly (bypassing the store). Registered exactly once.
 */
i18next.on("languageChanged", (lng) => {
  const base = (typeof lng === "string" ? lng.split("-")[0] : "")?.toLowerCase() ?? "";
  if (!isSupportedLocale(base)) return;
  if (useLocale.getState().locale !== base) {
    useLocale.setState({ locale: base });
    applyHtmlLang(base);
    persistLocale(base);
  }
});
